/**
 * Catálogo ÚNICO del estado consolidado a nivel PERSONA (asociado), distinto
 * del ciclo de vida del legajo (`estado-legajo.ts`). Una persona puede estar
 * "De baja" aunque su legajo siga "Dado de alta" (ej. un familiar fallecido en
 * un grupo que sigue activo), o "Bloqueada"/"Con error" individualmente.
 *
 * Los valores técnicos coinciden con el tipo `EstadoPersona` que calcula el
 * backend (`busqueda.service.ts`, helper `computarEstadoPersona`), con
 * prioridad: de baja › bloqueada › error › activa.
 */
import type { TonoEstado } from './estado-legajo';

export type EstadoPersona = 'activa' | 'baja' | 'no_en_binary' | 'bloqueada' | 'error';

export interface EstadoPersonaInfo {
  label: string;
  descripcion: string;
  tono: TonoEstado;
}

export const ESTADO_PERSONA: Record<EstadoPersona, EstadoPersonaInfo> = {
  activa: {
    label: 'Activa',
    descripcion: 'La persona no tiene baja, bloqueo ni error de validación.',
    tono: 'success',
  },
  baja: {
    label: 'De baja',
    descripcion:
      'La persona está dada de baja (fallecimiento u otro motivo), en el micrositio o detectada en Binary. Puede ocurrir aunque su legajo siga dado de alta.',
    tono: 'neutral',
  },
  no_en_binary: {
    label: 'No está en Binary',
    descripcion:
      'La persona figura dada de alta en el portal pero no existe en Binary de ninguna forma (ni activa, ni de baja, ni en el grupo del titular). Binary manda: no tiene cobertura.',
    tono: 'warning',
  },
  bloqueada: {
    label: 'Bloqueada',
    descripcion:
      'La persona está frenada individualmente: no avanza en el onboarding ni se incluye en el archivo de alta.',
    tono: 'error',
  },
  error: {
    label: 'Con error',
    descripcion:
      'La fila de la persona tiene un error de validación del padrón que impide cerrarla.',
    tono: 'warning',
  },
};

/** Info del estado, con fallback seguro para valores desconocidos. */
export function estadoPersonaInfo(estado: string): EstadoPersonaInfo {
  return (
    ESTADO_PERSONA[estado as EstadoPersona] ?? {
      label: estado,
      descripcion: 'Estado no reconocido.',
      tono: 'neutral',
    }
  );
}
